/**
 * Brand lockup — the TT speed monogram, copied path-for-path from
 * `brand/logo-mark.svg` and rendered exactly as the prototype does
 * (`design/personal-car-app/index.html`, `brandMark()`).
 *
 * Four polygons, `viewBox 0 0 100 100`: two for the graphite T, two for the racing-red
 * one. Do NOT redraw them — this glyph is the brand, and the mobile clients, the app
 * icon and the marketing files all carry these exact coordinates.
 *
 * Colours come from tokens so the mark inverts with the theme: graphite becomes the
 * near-white `ink` in dark mode, the accent brightens. That is the rule the source SVG
 * states in its own comment ("on dark backgrounds swap the graphite fills").
 */
export function BrandMark({ size = 44 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size * (70 / 80)}
      // viewBox straight from brand/logo-mark.svg, which hugs the glyph (x 15→85,
      // y 22→82) instead of padding it inside a 100×100 square. The prototype uses
      // the square and the mark comes out small and adrift in its own box; here the
      // height follows the 80×70 ratio so nothing is squashed or clipped.
      viewBox="10 17 80 70"
      role="img"
      aria-label="Twin Turbo"
      focusable="false"
    >
      <MarkPaths />
    </svg>
  );
}

function MarkPaths() {
  return (
    <>
      <path fill="var(--color-ink)" d="M18.92 22 L52.92 22 L49.69 36 L15.69 36 Z" />
      <path fill="var(--color-ink)" d="M28.92 22 L42.92 22 L30.68 75 L15.07 82 Z" />
      <path fill="var(--color-accent)" d="M50.92 22 L84.92 22 L81.69 36 L47.69 36 Z" />
      <path fill="var(--color-accent)" d="M60.92 22 L74.92 22 L62.68 75 L47.07 82 Z" />
    </>
  );
}

/**
 * Высота знака в шапке экрана — и у знака рядом с заголовком, и у знака внутри логотипа на главной.
 * Одна на обоих, иначе при переходе между вкладками знак менял бы размер.
 */
export const HEADER_MARK_HEIGHT = 26;

/**
 * Знак TT рядом с заголовком экрана.
 *
 * viewBox вплотную по глифу (x 15→85, y 22→82), без пяти единиц поля, которые оставляет
 * `BrandMark`: в шапке знак стоит в паре с заголовком, и пустое поле сбоку сдвинуло бы пару с оси
 * экрана. Декоративный — имя экрана читает заголовок рядом.
 */
export function HeaderMark() {
  return (
    <svg
      height={HEADER_MARK_HEIGHT}
      width={HEADER_MARK_HEIGHT * (70 / 60)}
      viewBox="15 22 70 60"
      aria-hidden="true"
      focusable="false"
      className="shrink-0"
    >
      <MarkPaths />
    </svg>
  );
}

/**
 * Логотип целиком — знак TT и надпись TWINTURBO, как в шапке twinturbo.pro и на наклейке бензовоза.
 *
 * Надпись в кривых, из `tt-docs/brand/lockup/twinturbo-logo-on-light.svg` (Space Grotesk 700 с
 * синтетическим наклоном): шрифт бренда в приложение не грузится, а живым текстом системным
 * шрифтом это был бы уже не логотип. Цвета — токены, как у `BrandMark`: графит в тёмной теме
 * становится почти белым `ink`, иначе TWIN пропал бы на тёмном фоне. Знак внутри логотипа ровно
 * `HEADER_MARK_HEIGHT` в высоту: у логотипа высота глифа знака и есть высота всего рисунка.
 */
export function BrandLogo() {
  return (
    <svg
      height={HEADER_MARK_HEIGHT}
      width={HEADER_MARK_HEIGHT * (136.619 / 22.286)}
      viewBox="2.026 1.857 136.619 22.286"
      aria-hidden="true"
      focusable="false"
      className="shrink-0"
    >
      <path fill="var(--color-ink)" d="M3.456 1.857L16.085 1.857L14.885 7.057L2.256 7.057Z" />
      <path fill="var(--color-ink)" d="M7.17 1.857L12.37 1.857L7.824 21.543L2.026 24.143Z" />
      <path fill="var(--color-accent)" d="M15.342 1.857L27.97 1.857L26.771 7.057L14.142 7.057Z" />
      <path fill="var(--color-accent)" d="M19.056 1.857L24.256 1.857L19.71 21.543L13.912 24.143Z" />
      <path
        fill="var(--color-ink)"
        d="M43.876 18.882 46.341 9.022H42.873L43.383 6.982H52.563L52.053 9.022H48.585L46.12 18.882ZM52.24 18.882 53.651 6.982H55.878L54.45 17.318H54.756L58.836 6.982H62.712L61.624 17.318H61.93L65.67 6.982H67.897L63.358 18.882H59.482L60.596 8.308H60.289L56.116 18.882ZM66.724 18.882 69.699 6.982H71.943L68.968 18.882ZM71.382 18.882 74.357 6.982H78.624L78.395 17.352H78.701L81.293 6.982H83.503L80.528 18.882H76.261L76.491 8.512H76.184L73.592 18.882Z"
      />
      <path
        fill="var(--color-accent)"
        d="M85.696 18.882 88.161 9.022H84.693L85.203 6.982H94.383L93.873 9.022H90.405L87.94 18.882ZM97.639 19.12Q96.177 19.12 95.265 18.585Q94.353 18.049 94.049 17.055Q93.746 16.06 94.086 14.7L96.015 6.982H98.259L96.312 14.768Q96.041 15.856 96.504 16.485Q96.967 17.114 98.14 17.114Q99.313 17.114 100.091 16.485Q100.869 15.856 101.141 14.768L103.087 6.982H105.331L103.401 14.7Q103.062 16.06 102.26 17.055Q101.459 18.049 100.28 18.585Q99.101 19.12 97.639 19.12ZM104.702 18.882 107.677 6.982H112.845Q113.967 6.982 114.702 7.373Q115.438 7.764 115.718 8.478Q115.999 9.192 115.756 10.161L115.705 10.365Q115.438 11.436 114.762 12.099Q114.086 12.762 113.262 13.068L113.185 13.374Q113.857 13.408 114.122 13.842Q114.388 14.275 114.209 14.989L113.236 18.882H110.992L111.885 15.312Q111.987 14.904 111.838 14.649Q111.689 14.394 111.196 14.394H108.068L106.946 18.882ZM108.578 12.354H111.264Q112.063 12.354 112.622 11.921Q113.181 11.487 113.359 10.773L113.402 10.603Q113.58 9.889 113.247 9.456Q112.913 9.022 112.097 9.022H109.411ZM115.276 18.882 115.769 16.91H117.333L119.322 8.954H117.758L118.251 6.982H124.371Q125.459 6.982 126.175 7.348Q126.891 7.713 127.174 8.385Q127.457 9.056 127.223 9.991L127.18 10.161Q126.976 10.977 126.541 11.495Q126.105 12.014 125.618 12.295Q125.132 12.575 124.728 12.694L124.652 13Q125 13.102 125.372 13.383Q125.744 13.663 125.927 14.19Q126.109 14.717 125.897 15.567L125.854 15.737Q125.608 16.723 124.972 17.428Q124.337 18.134 123.436 18.508Q122.535 18.882 121.464 18.882ZM119.594 16.842H121.702Q122.433 16.842 122.973 16.485Q123.513 16.128 123.678 15.465L123.721 15.295Q123.887 14.632 123.534 14.275Q123.181 13.918 122.433 13.918H120.325ZM120.835 11.878H122.909Q123.606 11.878 124.146 11.521Q124.686 11.164 124.843 10.535L124.885 10.365Q125.047 9.719 124.692 9.37Q124.337 9.022 123.623 9.022H121.549ZM131.639 19.12Q129.395 19.12 128.377 17.888Q127.359 16.655 127.933 14.36L128.647 11.504Q129.22 9.209 130.854 7.977Q132.489 6.744 134.733 6.744Q136.977 6.744 137.994 7.977Q139.012 9.209 138.439 11.504L137.725 14.36Q137.151 16.655 135.517 17.888Q133.883 19.12 131.639 19.12ZM132.14 17.114Q133.398 17.114 134.278 16.383Q135.158 15.652 135.464 14.428L136.212 11.436Q136.518 10.212 136.003 9.481Q135.489 8.75 134.231 8.75Q132.99 8.75 132.102 9.481Q131.214 10.212 130.908 11.436L130.16 14.428Q129.854 15.652 130.376 16.383Q130.899 17.114 132.14 17.114Z"
      />
    </svg>
  );
}

/** Wordmark: TWIN over TURBO, 22px/800, tracking .02em — metrics from the prototype. */
export function BrandWordmark() {
  return (
    <div
      className="flex flex-col items-center tracking-[0.02em]"
      aria-hidden="true"
    >
      <span className="text-[22px] font-extrabold leading-none text-ink">TWIN</span>
      <span className="text-[22px] font-extrabold leading-none text-accent">TURBO</span>
    </div>
  );
}

/**
 * Full lockup as it appears on the auth screens: mark, wordmark and an optional
 * tagline, stacked and centred with a 14px gap.
 */
export function BrandLockup({ size = 44, tagline }: { size?: number; tagline?: string }) {
  return (
    <div className="mb-1.5 flex flex-col items-center gap-3.5">
      <BrandMark size={size} />
      <BrandWordmark />
      {tagline && <p className="text-ink-soft">{tagline}</p>}
    </div>
  );
}
